import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../store/store";
import {
  createDrone,
  selectSelectedDrone,
  updateDroneById,
  DroneDTO,
  fetchDroneById,
  setSelectedDrone,
} from "../../store/drone_slice";
import Categories from "./common/steps/category/category";
import images from "../../common/header/assets/images";
import Navigation from "./common/navigation/navigation";
import AdvertisementForm from "./common/steps/advertisement/advertisement";
import JobSuitabilityForm from "./common/steps/job_description/job_description";
import DroneSpecsForm from "./common/steps/spec_step/spec_step";
import ExtraGear from "./common/steps/extra_gear/extra_gear";
import Location from "./common/steps/location/location";
import DronePhotos from "./common/steps/drone_photos/drone_photos";
import Prices from "./common/steps/prices/prices";
import RentalAvailability from "./common/steps/rental_availability/rental_availability";

interface StepProps {
  droneData: Omit<DroneDTO, "id" | "postedBy"> & {
    imageKeys: string[];
  };
  updateDroneData: (newData: Partial<Omit<DroneDTO, "id" | "postedBy">>) => void;
}

const getInitialDroneData = (): Omit<DroneDTO, "id" | "postedBy"> & {
  imageKeys: string[];
} => {
    const now = new Date();
    const formattedDateTime = now.toISOString().slice(0, 19);
    return {
      name: "",
      price: 0,
      currency: "RON",
      location: "",
      lat: 0,
      lng: 0,
      dateTime: formattedDateTime,
      description: "",
      extraGears: [],
      model: "",
      brand: "",
      extraDetails: "",
      imageKeys: [],
      imageFiles: [],
      specs: {},
      categories: [],
    };
};

const steps = [
  {
    id: 1,
    name: "Categories",
    hash: "#categories",
    component: (props: StepProps) => <Categories {...props} />,
  },
  {
    id: 2,
    name: "advertisement",
    hash: "#advertisement",
    component: (props: StepProps) => <AdvertisementForm {...props} />,
  },
  {
    id: 3,
    name: "description",
    hash: "#description",
    component: (props: StepProps) => <JobSuitabilityForm {...props} />,
  },
  {
    id: 4,
    name: "specifications",
    hash: "#specifications",
    component: (props: StepProps) => <DroneSpecsForm {...props} />,
  },
  {
    id: 5,
    name: "gear",
    hash: "#gear",
    component: (props: StepProps) => <ExtraGear {...props} />,
  },
  {
    id: 6,
    name: "location",
    hash: "#location",
    component: (props: StepProps) => <Location {...props} />,
  },
  {
    id: 7,
    name: "prices",
    hash: "#prices",
    component: (props: StepProps) => <Prices {...props} />,
  },
  {
    id: 8,
    name: "photos",
    hash: "#photos",
    component: (props: StepProps) => <DronePhotos {...props} />,
  },
  {
    id: 9,
    name: "availability",
    hash: "#availability",
    component: (props: StepProps) => <RentalAvailability {...props} />,
  },
];

import ErrorModal from "../../common/modal/error_modal";
import toast from "../../common/toast/toast_service";

const DroneServicePage: React.FC = () => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { id: routeId } = useParams<{ id?: string }>();
  const selectedDrone = useSelector(selectSelectedDrone);
  const isEditMode = Boolean(routeId);

  const [droneData, setDroneData] = React.useState<Omit<DroneDTO, "id" | "postedBy">>(getInitialDroneData());
  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [errorModalMessage, setErrorModalMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isEditMode && routeId) {
      const numericId = parseInt(routeId, 10);
      if (!isNaN(numericId)) {
        dispatch(fetchDroneById(numericId));
      }
    } else {
      setDroneData(getInitialDroneData());
      dispatch(setSelectedDrone(null));
    }
  }, [dispatch, routeId, isEditMode]);

  React.useEffect(() => {
    if (isEditMode && selectedDrone && selectedDrone.id.toString() === routeId) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, postedBy, ...droneWithoutIdAndUser } = selectedDrone;
      setDroneData(droneWithoutIdAndUser);
    }
  }, [selectedDrone, isEditMode, routeId]);

  React.useEffect(() => {
    if (steps[currentStep]) {
        window.location.hash = steps[currentStep]!.hash;
    }
  }, [currentStep]);

  React.useEffect(() => {
    const hash = window.location.hash;
    const stepIndex = steps.findIndex((step) => step.hash === hash);
    if (stepIndex !== -1) {
      setCurrentStep(stepIndex);
    } else {
      setCurrentStep(0);
      if(steps[0]) {
         window.location.hash = steps[0].hash;
      }
    }
  }, []);

  React.useEffect(() => {
    return () => {
      dispatch(setSelectedDrone(null));
    };
  }, [dispatch]);

  const updateDroneData = (newData: Partial<Omit<DroneDTO, "id" | "postedBy">>) => {
    setDroneData((prevData) => {
      const updatedData = {
        ...prevData,
        ...newData,
      };
      return updatedData;
    });
  };

  // Step validation logic for each step
  function validateStep(stepIdx: number, droneData: Omit<DroneDTO, "id" | "postedBy"> & { imageKeys: string[] }): true | string {
    switch (stepIdx) {
      case 0: // Categories
        if (!droneData.categories || droneData.categories.length === 0) {
          return "Please select at least one drone category.";
        }
        return true;
      case 1: // Advertisement
        if (!droneData.brand || !droneData.model || !droneData.name) {
          return "Please enter the drone brand, model, and headline/name.";
        }
        return true;
      case 2: // Description
        if (!droneData.description || droneData.description.trim().length < 10) {
          return "Please enter a job suitability/description of at least 10 characters.";
        }
        return true;
      case 3: // Specs
        if (!droneData.specs || Object.keys(droneData.specs).length === 0) {
          return "Please complete at least one specification for your drone.";
        }
        return true;
      case 4: // Extra Gear
        // Accept empty, extraGear is optional
        return true;
      case 5: // Location
        if (!droneData.location || !droneData.lat || !droneData.lng) {
          return "Please select a valid location on the map.";
        }
        return true;
      case 6: // Prices
        if (typeof droneData.price !== "number" || droneData.price <= 0) {
          return "Please set a valid rental price per day.";
        }
        if (!droneData.currency || !["RON", "EUR"].includes(String(droneData.currency))) {
          return "Please select a valid currency.";
        }
        return true;
      case 7: // Photos
        if (!droneData.imageKeys || droneData.imageKeys.length < 5) {
          return "Please add at least 5 photos.";
        }
        return true;
      case 8: // Availability
        // Could add checks for availability data if needed
        return true;
      default:
        return true;
    }
  }

  const nextStep = async () => {
    const valid = validateStep(currentStep, droneData);
    if (valid !== true) {
      setErrorModalMessage(valid || "Please complete the current step before continuing.");
      setErrorModalOpen(true);
      return;
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Prevent multiple submissions
      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      try {
        if (isEditMode && routeId) {
          const numericId = parseInt(routeId, 10);
          if (!isNaN(numericId)) {
            await dispatch(updateDroneById({ id: numericId, droneData })).unwrap();
          } else {
            setErrorModalMessage("Invalid ID for update: " + routeId);
            setErrorModalOpen(true);
            setIsSubmitting(false);
            return;
          }
        } else {
          await dispatch(createDrone(droneData)).unwrap();
          toast.showSuccess("Drone created successfully!");
        }

        // Only navigate if no error
        navigate("/");
      } catch (error: unknown) {
        // Show error in modal, do not redirect
        let message = "Failed to save drone. Please check your data and try again.";
        if (error) {
          if (typeof error === "string") {
            message = error;
          } else if (typeof error === "object" && error !== null) {
            const errorObj = error as { message?: string; validationErrors?: Record<string, string[]> };
            if (errorObj.message) {
              message = errorObj.message;
            }
            if (errorObj.validationErrors) {
              // Format validation errors for display
              const details = Object.entries(errorObj.validationErrors)
                .map(([field, errors]) => `${field}: ${errors.join(", ")}`)
                .join("\n");
              message += "\n\n" + details;
            }
          }
        }
        setErrorModalMessage(message);
        setErrorModalOpen(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const renderCurrentStep = () => {
    const stepConfig = steps[currentStep];
    if (!stepConfig) return <div>Step not found</div>;

    const StepComponent = stepConfig.component;
    
    // For the DronePhotos step, make sure we're passing imageKeys
    const dataForStep = {
      ...droneData,
      imageKeys: droneData.imageKeys || []
    };

    return StepComponent({ droneData: dataForStep, updateDroneData });
  };

  return (
    <div className="h-full flex flex-col justify-between">
      <ErrorModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        message={errorModalMessage}
      />
      <div className="p-10">
        <img
          src={images.DroneDenLogo}
          alt="DroneDen Logo"
          onClick={handleLogoClick}
          className="cursor-pointer"
        />
        <h1 className="text-3xl font-bold my-4">{isEditMode ? 'Edit Your Drone Listing' : 'Create a New Drone Listing'}</h1>
        <div className="mb-8">{renderCurrentStep()}</div>
      </div>
      <div className="w-full bottom-auto">
        <Navigation
          currentStep={currentStep}
          totalSteps={steps.length}
          onNext={nextStep}
          onPrevious={previousStep}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default DroneServicePage;
